import * as React from 'react';
import { styles } from './styles';
import { TransitionStatus } from 'react-transition-group/Transition';
import { animations } from './animations';
import { makeDebugger } from '@lib';
import { Subscription } from 'rxjs/internal/Subscription';
import { fromEvent } from 'rxjs/internal/observable/fromEvent';
import { filter } from 'rxjs/internal/operators/filter';
import { ModalProps, ModalCloseContextTarget } from '../types';

const debug = makeDebugger('ModalInner');

interface Props
  extends Pick<ModalProps, 'open' | 'size' | 'backdrop' | 'disableBackdropClick' | 'disableEscapeKeydown'> {
  scrolling: boolean;
  forwardedRef: React.RefObject<HTMLDivElement>;
  transitionStatus: TransitionStatus;
  onCloseRequest: ModalProps['onClose'];
}

export class ModalInner extends React.Component<Props> {
  private backdropClickSubscription?: Subscription;
  private escapeKeydownSubscription?: Subscription;

  // ----------------------------------------
  // Lifecycle
  // ----------------------------------------

  componentDidUpdate(prevProps: Props) {
    if (prevProps.transitionStatus === 'entering' && this.props.transitionStatus === 'entered') {
      this.subscribeEventListeners();
    } else if (prevProps.open && !this.props.open) {
      this.unsubscribeEventListeners();
    }
  }

  // ----------------------------------------
  // Event (un)subscriptions
  // ----------------------------------------

  subscribeEventListeners = () => {
    debug('subscribeEventListeners()');
    if (!this.props.disableBackdropClick) {
      this.observeBackdropClick();
    }

    if (!this.props.disableEscapeKeydown) {
      this.observeEscapeKeydown();
    }
  };

  unsubscribeEventListeners = () => {
    debug('unsubscribeToEventSubscriptions()');
    if (this.backdropClickSubscription) {
      this.backdropClickSubscription.unsubscribe();
    }

    if (this.escapeKeydownSubscription) {
      this.escapeKeydownSubscription.unsubscribe();
    }
  };

  // Event Listeners + resolvers
  // TODO: Look into using react hooks
  observeBackdropClick = () => {
    this.backdropClickSubscription = fromEvent(window, 'click')
      .pipe(
        filter(e => {
          return (
            !this.props.disableBackdropClick &&
            this.props.open &&
            !!e.target &&
            !!this.props.forwardedRef.current &&
            (!(this.props.forwardedRef.current as any).contains(e.target) &&
              !(e.target as HTMLElement).closest('.modal__content'))
          );
        })
      )
      .subscribe(e => {
        debug('observeBackdropClick()');
        this.handleCloseRequest(e as any, 'backdrop');
      });
  };

  observeEscapeKeydown = () => {
    this.escapeKeydownSubscription = fromEvent(document, 'keydown')
      .pipe(
        filter((e: any) => {
          return !this.props.disableEscapeKeydown && this.props.open && e.keyCode === 27;
        })
      )
      .subscribe(e => {
        debug('observeEscapeKeydown()');
        this.handleCloseRequest(e, 'escape');
      });
  };

  private handleCloseRequest = (e: React.MouseEvent<HTMLElement>, target: ModalCloseContextTarget) => {
    this.props.onCloseRequest(e, { target });
  };

  // ----------------------------------------
  // Render
  // ----------------------------------------

  render() {
    return (
      <div
        ref={this.props.forwardedRef}
        className={styles.container(this.props.size, this.props.scrolling)}
        style={{ ...animations.modal[this.props.transitionStatus] }}
      >
        {this.props.children}
      </div>
    );
  }
}
